import React, { useState, useEffect } from 'react';
import { X, Save, Shield, Check, AlertTriangle } from 'lucide-react';

const RoleEditorModal = ({ isOpen, onClose, role, onSave, allPermissions }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: [],
        isActive: true
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (role) {
                setFormData({
                    name: role.name,
                    description: role.description || '',
                    permissions: role.permissions || [],
                    isActive: role.isActive ?? true
                });
            } else {
                setFormData({
                    name: '',
                    description: '',
                    permissions: [],
                    isActive: true
                });
            }
            setErrors({});
        }
    }, [isOpen, role]);

    const handlePermissionToggle = (permission) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permission)
                ? prev.permissions.filter(p => p !== permission)
                : [...prev.permissions, permission]
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Rol adı zorunludur';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            onSave({ ...formData, id: role?.id });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#252525]">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Shield className="w-5 h-5 text-orange-500" />
                        {role ? 'Rolü Düzenle' : 'Yeni Rol Oluştur'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Rol Adı <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                disabled={role?.name === 'root'}
                                className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="Örn: Editor"
                            />
                            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Açıklama</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={3}
                                className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors resize-none"
                                placeholder="Rol hakkında açıklama..."
                            />
                        </div>

                        <div className="flex items-center gap-3 p-4 bg-[#0a0e13] rounded-lg border border-white/5">
                            <div className="flex-1">
                                <label className="text-sm font-medium text-white block">Rol Durumu</label>
                                <p className="text-xs text-gray-500">Pasif roller kullanıcılara atanamaz ve etkisizdir.</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none ring-0 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                            </label>
                        </div>
                    </div>

                    <div className="border-t border-white/10 pt-4">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-orange-400" />
                            Yetkiler
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                            {allPermissions.map((permission) => (
                                <label
                                    key={permission}
                                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${formData.permissions.includes(permission) ? 'bg-orange-500/10 border-orange-500/30' : 'bg-[#0a0e13] border-white/5 hover:border-white/20'}`}
                                >
                                    <div className="relative flex items-center mt-0.5">
                                        <input
                                            type="checkbox"
                                            className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-gray-500 checked:bg-orange-500 checked:border-orange-500 transition-all"
                                            checked={formData.permissions.includes(permission)}
                                            onChange={() => handlePermissionToggle(permission)}
                                        />
                                        <Check className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity" />
                                    </div>
                                    <div className="flex-1">
                                        <span className={`text-sm font-medium ${formData.permissions.includes(permission) ? 'text-orange-400' : 'text-gray-300'}`}>
                                            {permission}
                                        </span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="p-4 border-t border-white/10 bg-[#252525] flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                        İptal
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Save className="w-4 h-4" />
                        Kaydet
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RoleEditorModal;
